'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';

export function AboutSection() {
  const openCalendly = () => {
    // same behavior used elsewhere in site to trigger Calendly badge programmatically
    const badge = document.getElementsByClassName('calendly-badge-content')[0] as HTMLElement | undefined;
    if (badge) badge.click();
    else window.open('https://calendly.com/3xerriestech/free-audit', '_blank');
  };

  return (
    <section className="w-full py-20 sm:py-28 lg:py-36 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-cyan-500/5 rounded-full -translate-x-36 -translate-y-36"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/5 rounded-full translate-x-48 translate-y-48"></div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-500/10 text-cyan-600 text-sm font-medium mb-6">
            <span className="w-2 h-2 bg-cyan-500 rounded-full mr-2"></span>
            Why Choose AuditLeads
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-foreground mb-6 font-headline max-w-4xl mx-auto">
            We Help You Convert More <span className="text-cyan-600">Visitors Into Customers</span>
          </h2>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mb-16">
          {/* Stat Card 1 */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center transform hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl font-bold text-red-500">97%</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Visitors Leave</h3>
            <p className="text-gray-600 text-sm">Most websites lose 97% of their visitors without converting them into customers</p>
          </div>

          {/* Stat Card 2 */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center transform hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-cyan-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl font-bold text-cyan-600">48h</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Fast Results</h3>
            <p className="text-gray-600 text-sm">Get your detailed audit report with actionable insights in under 48 hours</p>
          </div>

          {/* Stat Card 3 */}
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center transform hover:scale-105 transition-all duration-300">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl font-bold text-blue-600">+3x</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">More Conversions</h3>
            <p className="text-gray-600 text-sm">Our clients typically see 3x more conversions after implementing our recommendations</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <div className="space-y-6">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Stop Guessing. Start Converting.
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              Our AI-powered audit identifies the exact conversion blockers on your site and provides a step-by-step plan to fix them. No more wondering why visitors aren't becoming customers.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              In just 48 hours, you'll have a clear roadmap to increase your sales with proven strategies that actually work.
            </p>
            
            {/* Feature List */}
            <div className="space-y-4 mt-8">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-cyan-600 text-sm font-bold">✓</span>
                </div>
                <span className="text-gray-700">Detailed conversion analysis</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-cyan-600 text-sm font-bold">✓</span>
                </div>
                <span className="text-gray-700">Step-by-step improvement plan</span>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-cyan-600 text-sm font-bold">✓</span>
                </div>
                <span className="text-gray-700">Proven strategies that work</span>
              </div>
            </div>

            <Button size="lg" onClick={openCalendly} className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-4 text-lg font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200">
              Start Finding Leads Today
            </Button>
          </div>

          {/* Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl transform rotate-2 hover:rotate-0 transition-all duration-500">
              <Image
                src="https://firebasestorage.googleapis.com/v0/b/auditleats.firebasestorage.app/o/compressed_1752253482922.webp?alt=media&token=38e3c873-b417-4c04-b38a-2040eebb66dc"
                alt="Website audit illustration"
                width={600}
                height={450}
                className="object-cover w-full h-full"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 to-transparent"></div>
            </div>
            {/* Floating Badge */}
            <div className="absolute -top-4 -right-4 bg-cyan-600 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
              100% Free
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
